import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { getTimer, stopTimerStore, subscribeTimer } from "@/lib/timer";
import { fmtElapsed, todayIso } from "@/lib/time";
import { Timer as TimerIcon, Square } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/i18n";

export async function saveTimerEntry(timer) {
  const minutes = Math.max(1, Math.round((Date.now() - timer.started_at) / 60000));
  const { data } = await api.post(`/matters/${timer.matter_id}/time-entries`, {
    date: todayIso(),
    description: timer.description || "Timer entry",
    actual_minutes: minutes,
    billable: true,
    billing_status: "Unbilled",
  });
  stopTimerStore();
  return data;
}

export default function TimerWidget() {
  const { t } = useT();
  const [timer, setTimer] = useState(getTimer());
  const [now, setNow] = useState(Date.now());
  const [stopping, setStopping] = useState(false);
  const navigate = useNavigate();

  useEffect(() => subscribeTimer(() => setTimer(getTimer())), []);
  useEffect(() => {
    if (!timer) return;
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, [timer]);

  if (!timer) return null;

  const stop = async () => {
    setStopping(true);
    try {
      const entry = await saveTimerEntry(timer);
      toast.success(
        t("timer.saved", { h: (entry.billable_minutes / 60).toFixed(1), amount: entry.amount.toFixed(2) })
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setStopping(false);
    }
  };

  return (
    <div
      data-testid="timer-widget"
      className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:bottom-5 sm:right-5 z-50 bg-slate-900 text-white rounded-lg shadow-xl px-4 py-3 sm:w-72 border border-slate-700"
    >
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
        </span>
        {t("timer.widget_running")}
      </div>
      <button
        onClick={() => navigate(`/matters/${timer.matter_id}`)}
        className="mt-1.5 text-sm font-semibold text-left hover:underline truncate w-full"
        data-testid="timer-widget-matter"
      >
        {timer.matter_name}
      </button>
      {timer.description && (
        <div className="text-xs text-slate-400 truncate">{timer.description}</div>
      )}
      <div className="mt-2 flex items-center justify-between">
        <span className="font-mono text-lg" data-testid="timer-widget-elapsed">
          {fmtElapsed(now - timer.started_at)}
        </span>
        <button
          data-testid="timer-widget-stop-button"
          onClick={stop}
          disabled={stopping}
          className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-slate-900 text-xs font-bold px-3 py-1.5 rounded-md transition-colors"
        >
          <Square className="h-3 w-3" /> {stopping ? t("timer.saving") : t("timer.stop_short")}
        </button>
      </div>
    </div>
  );
}
